import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  PlusCircle, DollarSign, Trash2, Pencil, AlertTriangle,
  ArrowLeft, TrendingUp, CheckCircle2, Clock, BarChart2,
} from 'lucide-react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { useToast } from '@/components/ui/use-toast';
import Pagination from '@/components/Pagination';
import CostoFormModal from './CostoFormModal';
import ParticipacionVentasModal from './ParticipacionVentasModal';
import ResumenMesModal from './ResumenMesModal';
import { fetchCentrosCostos, deleteCentroCosto } from './services/centroCostos.services';
import { getVentasByPeriodo } from './services/ventasPeriodo.services';

const LIMIT = 10;
const MESES = [
  { value: 1, label: 'Enero' }, { value: 2, label: 'Febrero' }, { value: 3, label: 'Marzo' },
  { value: 4, label: 'Abril' }, { value: 5, label: 'Mayo' }, { value: 6, label: 'Junio' },
  { value: 7, label: 'Julio' }, { value: 8, label: 'Agosto' }, { value: 9, label: 'Septiembre' },
  { value: 10, label: 'Octubre' }, { value: 11, label: 'Noviembre' }, { value: 12, label: 'Diciembre' },
];

const now = new Date();

const nivelLabel = { empresa: 'Empresa', centro: 'Centro', cliente: 'Cliente' };
const nivelColor = {
  empresa: 'bg-violet-500/15 text-violet-700 dark:text-violet-300',
  centro: 'bg-blue-500/15 text-blue-700 dark:text-blue-300',
  cliente: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300',
};

const formatCurrency = (value) =>
  value != null ? `$${Number(value).toLocaleString('es-CO')}` : '—';

const getMonthBounds = (año, mes) => {
  const first = `${año}-${String(mes).padStart(2, '0')}-01`;
  const lastDay = new Date(año, mes, 0).getDate();
  const last = `${año}-${String(mes).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;
  return { first, last };
};

const CentroCostosPage = () => {
  const { toast } = useToast();
  const navigate = useNavigate();

  // Período como año + mes separados
  const [año, setAño] = useState(now.getFullYear());
  const [mes, setMes] = useState(now.getMonth() + 1);
  const periodoActivo = `${año}-${String(mes).padStart(2, '0')}`;

  const [periodoData, setPeriodoData] = useState(null);
  const [loadingPeriodo, setLoadingPeriodo] = useState(false);
  const [participacionModalOpen, setParticipacionModalOpen] = useState(false);
  const [resumenModalOpen, setResumenModalOpen] = useState(false);

  // Gastos table
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentItem, setCurrentItem] = useState(null);
  const [modalMode, setModalMode] = useState('create');
  const [deleteTarget, setDeleteTarget] = useState(null);

  // Filtros (controlados externamente por el período activo)
  const [filterNivel, setFilterNivel] = useState('');
  const [filterCategoria, setFilterCategoria] = useState('');
  const [filterFechaDesde, setFilterFechaDesde] = useState('');
  const [filterFechaHasta, setFilterFechaHasta] = useState('');

  // ── Sincronizar filtros de fecha con el período activo ───────────────────
  useEffect(() => {
    const { first, last } = getMonthBounds(año, mes);
    setFilterFechaDesde(first);
    setFilterFechaHasta(last);
    setCurrentPage(1);
  }, [año, mes]);

  // ── Cargar datos del período de participación ────────────────────────────
  const cargarPeriodo = useCallback(async (p) => {
    if (!p) return;
    setLoadingPeriodo(true);
    try {
      const res = await getVentasByPeriodo(p);
      setPeriodoData(res.data || null);
    } catch {
      setPeriodoData(null);
    } finally {
      setLoadingPeriodo(false);
    }
  }, []);

  useEffect(() => { cargarPeriodo(periodoActivo); }, [periodoActivo, cargarPeriodo]);

  // ── Gastos ───────────────────────────────────────────────────────────────
  const buildQuery = useCallback(() => {
    const q = {};
    if (filterNivel) q.nivel = filterNivel;
    if (filterCategoria) q.categoria = filterCategoria;
    if (filterFechaDesde) q.fechaDesde = filterFechaDesde;
    if (filterFechaHasta) q.fechaHasta = filterFechaHasta;
    return q;
  }, [filterNivel, filterCategoria, filterFechaDesde, filterFechaHasta]);

  const loadData = useCallback(async () => {
    setIsLoading(true);
    try {
      const result = await fetchCentrosCostos({ limit: LIMIT, offset: currentPage, query: buildQuery() });
      setData(result.data || []);
      setTotalItems(result.totalRecords || 0);
    } catch (err) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, buildQuery, toast]);

  useEffect(() => { loadData(); }, [loadData]);

  const openCreate = () => { setCurrentItem(null); setModalMode('create'); setIsModalOpen(true); };
  const openEdit = (item) => { setCurrentItem(item); setModalMode('edit'); setIsModalOpen(true); };

  const handleDelete = async () => {
    try {
      await deleteCentroCosto(deleteTarget._id);
      toast({ title: 'Costo eliminado' });
      setDeleteTarget(null);
      loadData();
    } catch (err) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    }
  };

  const totalPages = Math.ceil(totalItems / LIMIT);
  const mesLabel = MESES.find(m => m.value === mes)?.label || '';

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="p-4 space-y-4"
    >
      {/* ── ENCABEZADO ─────────────────────────────────────────────────────── */}
      <Card>
        <CardHeader className="pb-4">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            {/* Título */}
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="icon" onClick={() => navigate('/financiera')} className="shrink-0">
                <ArrowLeft className="h-5 w-5" />
              </Button>
              <div className="bg-emerald-500/15 p-2 rounded-lg shrink-0">
                <DollarSign className="h-6 w-6 text-emerald-600 dark:text-emerald-400" />
              </div>
              <div>
                <CardTitle>Centro de Costos</CardTitle>
                <CardDescription>Distribución de gastos por participación de ventas</CardDescription>
              </div>
            </div>

            {/* Controles de período */}
            <div className="flex flex-wrap items-center gap-2 shrink-0">
              {/* Año */}
              <div className="flex items-center gap-1.5">
                <Label className="text-xs text-muted-foreground whitespace-nowrap">Año</Label>
                <Input
                  type="number"
                  className="h-8 w-20 text-sm text-center"
                  value={año}
                  min={2020}
                  max={2099}
                  onChange={e => setAño(Number(e.target.value))}
                />
              </div>
              {/* Mes */}
              <div className="flex items-center gap-1.5">
                <Label className="text-xs text-muted-foreground">Mes</Label>
                <Select value={String(mes)} onValueChange={v => setMes(Number(v))}>
                  <SelectTrigger className="h-8 w-36 text-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {MESES.map(m => (
                      <SelectItem key={m.value} value={String(m.value)}>{m.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Botón ver resumen */}
              <Button
                variant="outline"
                size="sm"
                className="h-8 gap-1.5"
                onClick={() => setResumenModalOpen(true)}
              >
                <BarChart2 className="h-3.5 w-3.5" />
                Ver ventas y gastos
              </Button>
            </div>
          </div>

          {/* ── Estado del período de ventas ── */}
          <div className="mt-3 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 pt-3 border-t border-border/50">
            <div className="flex items-center gap-3">
              <div className="bg-blue-500/15 p-1.5 rounded-lg">
                <TrendingUp className="h-4 w-4 text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <p className="text-sm font-medium flex items-center gap-2">
                  Ventas — {mesLabel} {año}
                  {loadingPeriodo ? (
                    <span className="text-xs font-normal text-muted-foreground flex items-center gap-1">
                      <Loader2Inline /> Verificando...
                    </span>
                  ) : periodoData ? (
                    <span className="inline-flex items-center gap-1 text-xs font-normal text-emerald-600 dark:text-emerald-400">
                      <CheckCircle2 className="h-3.5 w-3.5" /> Registrado
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-xs font-normal text-amber-600 dark:text-amber-400">
                      <Clock className="h-3.5 w-3.5" /> Pendiente
                    </span>
                  )}
                </p>
                {periodoData ? (
                  <p className="text-xs text-muted-foreground">
                    {periodoData.clientes?.length} clientes · {formatCurrency(periodoData.totalVentas)} en ventas totales
                  </p>
                ) : (
                  <p className="text-xs text-muted-foreground">
                    Registra las ventas para distribuir gastos por participación
                  </p>
                )}
              </div>
            </div>

            <div className="flex items-center gap-2">
              <Button
                size="sm"
                variant={periodoData ? 'outline' : 'default'}
                className="h-8"
                onClick={() => setParticipacionModalOpen(true)}
              >
                {periodoData ? 'Editar ventas' : 'Registrar ventas'}
              </Button>
              <Button size="sm" className="h-8" onClick={openCreate}>
                <PlusCircle className="mr-1.5 h-3.5 w-3.5" />
                Nuevo Gasto
              </Button>
            </div>
          </div>
        </CardHeader>
      </Card>

      {/* ── GASTOS ─────────────────────────────────────────────────────────── */}
      <Card>
        <CardContent className="pt-4 space-y-4">
          {/* Filtros adicionales (nivel y categoría; fechas ya controladas por el período) */}
          <div className="flex flex-wrap items-end gap-3">
            <div className="flex flex-col gap-1 min-w-[140px]">
              <Label className="text-xs">Nivel</Label>
              <Select value={filterNivel || '_all'} onValueChange={v => setFilterNivel(v === '_all' ? '' : v)}>
                <SelectTrigger className="h-8 text-sm">
                  <SelectValue placeholder="Todos" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="_all">Todos</SelectItem>
                  <SelectItem value="empresa">Empresa</SelectItem>
                  <SelectItem value="centro">Centro</SelectItem>
                  <SelectItem value="cliente">Cliente</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex flex-col gap-1 min-w-[180px]">
              <Label className="text-xs">Categoría</Label>
              <Select value={filterCategoria || '_all'} onValueChange={v => setFilterCategoria(v === '_all' ? '' : v)}>
                <SelectTrigger className="h-8 text-sm">
                  <SelectValue placeholder="Todas" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="_all">Todas</SelectItem>
                  {['Nómina', 'Arriendo', 'Servicios Públicos', 'Mantenimiento', 'Administrativo', 'Otro'].map(c => (
                    <SelectItem key={c} value={c}>{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <p className="text-xs text-muted-foreground pb-1">
              Mostrando gastos de <strong>{mesLabel} {año}</strong>
            </p>
          </div>

          {/* Tabla */}
          <div className="overflow-x-auto rounded-md border border-border">
            <table className="w-full text-sm">
              <thead className="bg-muted/50">
                <tr>
                  <th className="px-3 py-2 text-left font-medium text-muted-foreground">Fecha</th>
                  <th className="px-3 py-2 text-left font-medium text-muted-foreground">Descripción</th>
                  <th className="px-3 py-2 text-left font-medium text-muted-foreground">Categoría</th>
                  <th className="px-3 py-2 text-left font-medium text-muted-foreground">Nivel</th>
                  <th className="px-3 py-2 text-right font-medium text-muted-foreground">Costo Total</th>
                  <th className="px-3 py-2 text-center font-medium text-muted-foreground"># Centros</th>
                  <th className="px-3 py-2 text-right font-medium text-muted-foreground">Costo/Centro</th>
                  <th className="px-3 py-2 text-center font-medium text-muted-foreground"># Clientes</th>
                  <th className="px-3 py-2 text-right font-medium text-muted-foreground">Costo/Cliente</th>
                  <th className="px-3 py-2 text-center font-medium text-muted-foreground">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  Array.from({ length: 5 }).map((_, i) => (
                    <tr key={i} className="border-t border-border">
                      {Array.from({ length: 9 }).map((_, j) => (
                        <td key={j} className="px-3 py-2">
                          <div className="h-4 bg-muted animate-pulse rounded" />
                        </td>
                      ))}
                    </tr>
                  ))
                ) : data.length === 0 ? (
                  <tr>
                    <td colSpan={10} className="px-3 py-8 text-center text-muted-foreground">
                      No hay gastos registrados para {mesLabel} {año}
                    </td>
                  </tr>
                ) : (
                  data.map((row) => (
                    <tr key={row._id} className="border-t border-border hover:bg-muted/30 transition-colors">
                      <td className="px-3 py-2 whitespace-nowrap">
                        {row.fecha ? new Date(row.fecha).toLocaleDateString('es-CO') : '—'}
                      </td>
                      <td className="px-3 py-2 max-w-[200px] truncate" title={row.descripcion}>
                        {row.descripcion}
                      </td>
                      <td className="px-3 py-2">
                        <span className="text-xs px-2 py-0.5 rounded-full bg-muted">{row.categoria}</span>
                      </td>
                      <td className="px-3 py-2">
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${nivelColor[row.nivel] || ''}`}>
                          {nivelLabel[row.nivel] || row.nivel}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-right font-medium tabular-nums">{formatCurrency(row.costoGeneral)}</td>
                      <td className="px-3 py-2 text-center">{row.numeroCentros ?? '—'}</td>
                      <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">{formatCurrency(row.costoPorCentro)}</td>
                      <td className="px-3 py-2 text-center">{row.numeroClientes ?? '—'}</td>
                      <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">{formatCurrency(row.costoPorCliente)}</td>
                      <td className="px-3 py-2">
                        <div className="flex items-center justify-center gap-1">
                          <Button variant="ghost" size="icon" className="h-7 w-7 text-green-600 hover:text-green-700" onClick={() => openEdit(row)}>
                            <Pencil className="h-3.5 w-3.5" />
                          </Button>
                          <Button variant="ghost" size="icon" className="h-7 w-7 text-red-500 hover:text-red-600" onClick={() => setDeleteTarget(row)}>
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} />
          )}
        </CardContent>
      </Card>

      {/* Modales */}
      <ResumenMesModal
        isOpen={resumenModalOpen}
        onClose={() => setResumenModalOpen(false)}
        periodo={periodoActivo}
        periodoData={periodoData}
      />

      <ParticipacionVentasModal
        isOpen={participacionModalOpen}
        onClose={() => setParticipacionModalOpen(false)}
        onSaved={(p) => { setParticipacionModalOpen(false); cargarPeriodo(p); }}
        periodoExistente={periodoData}
      />

      <CostoFormModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSaved={() => { setIsModalOpen(false); loadData(); }}
        item={currentItem}
        mode={modalMode}
        periodoActivo={periodoActivo}
        periodoData={periodoData}
      />

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-red-500" />
              Eliminar costo
            </AlertDialogTitle>
            <AlertDialogDescription>
              ¿Estás seguro de que deseas eliminar este registro? Esta acción no se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">Eliminar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </motion.div>
  );
};

// Inline spinner para no importar Loader2 dos veces en JSX inline
const Loader2Inline = () => (
  <svg className="animate-spin h-3 w-3" fill="none" viewBox="0 0 24 24">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
  </svg>
);

export default CentroCostosPage;
